import { type ComponentType, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { usePageContentId } from '@confluence/page-context';
import type { ViewPagePerformanceModeState } from '@confluence/page-context/entry-points/viewPagePerformanceMode.ts';
import {
	claimViewPagePerformanceModeNotification,
	clearViewPagePerformanceMode,
	publishViewPagePerformanceMode,
} from '@confluence/page-context/entry-points/viewPagePerformanceModeStore.ts';
import {
	useHistory,
	useMatchRoute,
	useTransitionId,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { useNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy.ts';

import { trackViewPagePerformanceModeVisit } from './viewPagePerformanceModeVisit.ts';

const i18n = defineMessages({
	title: {
		id: 'content-renderer.performance-mode.notification.title',
		defaultMessage: 'Performance mode is on',
		description: 'Title of the informational notice when viewing a large page in performance mode',
	},
	description: {
		id: 'content-renderer.performance-mode.notification.description',
		defaultMessage:
			'This is a large page. Performance mode simplifies some features to help it load faster. Your page content has not changed.',
		description:
			'Explains why performance mode is active and reassures readers that content is unchanged',
	},
	learnMore: {
		id: 'content-renderer.performance-mode.notification.learn-more',
		defaultMessage: 'Learn more',
		description: 'Link to documentation about viewing large pages in performance mode',
	},
});

type Props = {
	contentId: string;
	state: ViewPagePerformanceModeState | null;
	isHydrating: boolean;
};

export const ViewPagePerformanceModeController: ComponentType<Props> = withFlags(
	({ contentId, state, isHydrating, flags }: Props & WithFlagsProps): null => {
		const [routeContentId] = usePageContentId();
		const transitionId = useTransitionId();
		const history = useHistory();
		const matchRoute = useMatchRoute();
		const [owner] = useState(() => Symbol('view-page-performance-mode'));
		const { formatMessage } = useIntl();
		const { shimUrl } = useNavigationPolicy();
		const isMainPage = routeContentId === contentId;
		const isActive = Boolean(state?.isActive);
		const learnMoreLink = state?.learnMoreLink ?? '';

		useEffect(() => {
			if (isMainPage && state) {
				const visit = trackViewPagePerformanceModeVisit(contentId, history, matchRoute);
				if (!visit) {
					clearViewPagePerformanceMode(owner);
					return;
				}
				publishViewPagePerformanceMode(owner, state, transitionId);
				if (!state.isActive) {
					visit.cancelNotification?.();
				}
			} else {
				clearViewPagePerformanceMode(owner);
			}
		}, [isMainPage, state, owner, transitionId, contentId, history, matchRoute]);
		useEffect(() => () => clearViewPagePerformanceMode(owner), [owner]);

		useEffect(() => {
			if (!isMainPage || !isActive || isHydrating) {
				return;
			}
			const visit = trackViewPagePerformanceModeVisit(contentId, history, matchRoute);
			if (!visit) {
				return;
			}
			const flagId = `view-page-performance-mode:${visit.id}:${contentId}`;
			// Defer until after commit. Strict Mode's effect replay cancels the first timer,
			// rather than claiming a notification that is immediately removed by cleanup.
			const timeout = setTimeout(() => {
				if (
					visit.hasEnded ||
					!claimViewPagePerformanceModeNotification(owner, contentId, transitionId)
				) {
					return;
				}
				let cancelled = false;
				visit.cancelNotification = () => {
					cancelled = true;
					void flags.hideFlag(flagId);
				};
				void flags
					.showFlag({
						id: flagId,
						type: 'info',
						close: 'auto',
						autoDismissSeconds: 8,
						title: formatMessage(i18n.title),
						description: formatMessage(i18n.description),
						actions: learnMoreLink
							? [
									{
										content: formatMessage(i18n.learnMore),
										href: shimUrl(learnMoreLink),
										target: '_blank',
									},
								]
							: [],
					})
					.then(() => {
						// showFlag can be queued behind another flag. Remove a late arrival
						// even when navigation happened before its promise resolved.
						if (cancelled) {
							void flags.hideFlag(flagId);
						}
					});
			}, 0);

			// A re-render or remount must not dismiss a requested notice. The visit's
			// route listener handles leaving the page, including late queued arrivals.
			return () => clearTimeout(timeout);
		}, [
			isMainPage,
			isActive,
			isHydrating,
			owner,
			contentId,
			transitionId,
			learnMoreLink,
			formatMessage,
			flags,
			shimUrl,
			history,
			matchRoute,
		]);

		return null;
	},
);
